import React, { useState, useMemo, useEffect } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StableMuiDialog from '../../common/StableMuiDialog.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from '../../common/toast';
import { axiosInstanceNoToast } from '../../fetchConfig/axiosConfig';
import { useUser, userObject } from '../UserContext';
import { sendAssignmentNotification } from '../../common/postAssignmentHelper';
import logger from '../../utils/logger';

const colors = {
  bgPrimary: '#ffffff',
  bgSecondary: '#f1efe8',
  textPrimary: '#1a1a18',
  textSecondary: '#5f5e5a',
  textTertiary: '#888780',
  borderTertiary: 'rgba(0,0,0,0.12)',
  borderSecondary: 'rgba(0,0,0,0.22)',
  accent: '#185FA5',
  accentBg: '#E6F1FB',
  accentBorder: '#B5D4F4',
  error: '#c62828',
};

const SectionLabel = ({ children }) => (
  <Typography
    component="p"
    sx={{
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: colors.textTertiary,
      m: '0 0 8px',
    }}
  >
    {children}
  </Typography>
);

const subtleButtonSx = {
  fontSize: 12,
  fontWeight: 500,
  color: colors.textSecondary,
  border: `0.5px solid ${colors.borderSecondary}`,
  borderRadius: '8px',
  px: 1.25,
  py: 0.5,
  textTransform: 'none',
  minWidth: 'auto',
  lineHeight: 1.4,
};

const isTaskTypeIncluded = (selectedTaskTypes, type) => selectedTaskTypes[type] !== false;

const ASSIGN_NONE = '__NONE__';
const UNASSIGN = '__UNASSIGN__';

const TASK_ASSIGNMENT_EXCLUDED_USER_NAMES = new Set([
  'brennan',
  'catherine',
  'chris',
  'christina',
  'jim',
  'oscar',
  'peter',
  'rogendy',
  'town portal',
]);

const isExcludedTaskAssignee = (userName) =>
  TASK_ASSIGNMENT_EXCLUDED_USER_NAMES.has((userName || '').trim().toLowerCase());

const isUnassignSelection = (selectedUserId) => selectedUserId === UNASSIGN;

const hasAssigneeSelection = (selectedUserId) =>
  selectedUserId !== ASSIGN_NONE && selectedUserId !== UNASSIGN;

// Custom date input component for DatePicker
const CustomDateInput = React.forwardRef(({ value, onClick, placeholder, style, ...props }, ref) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      height: style?.height || '36px',
      px: 1.25,
      border: style?.border || `0.5px solid ${colors.borderSecondary}`,
      borderRadius: style?.borderRadius || '8px',
      bgcolor: style?.background || colors.bgPrimary,
      cursor: 'pointer',
      boxSizing: 'border-box',
      width: '100%',
      position: 'relative',
      '&:hover': {
        borderColor: colors.textTertiary,
      },
    }}
  >
    <Box
      component="input"
      {...props}
      ref={ref}
      value={value || ''}
      readOnly
      placeholder={placeholder}
      sx={{
        flex: 1,
        border: 'none',
        outline: 'none',
        bgcolor: 'transparent',
        fontSize: style?.fontSize || '13px',
        fontFamily: 'inherit',
        lineHeight: 1.5,
        color: value ? colors.textPrimary : 'transparent',
        cursor: 'pointer',
        minWidth: 0,
        p: 0,
        '&::placeholder': {
          color: colors.textTertiary,
          opacity: 1,
        },
      }}
    />
    {!value && (
      <Typography
        sx={{
          position: 'absolute',
          left: 10,
          right: 28,
          fontSize: 13,
          color: colors.textTertiary,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        Click to select a date
      </Typography>
    )}
    <CalendarTodayIcon sx={{ fontSize: 16, color: colors.textTertiary, flexShrink: 0 }} />
  </Box>
));

const AssignmentDialog = ({ 
  open, 
  onClose, 
  selectedItems, 
  isAdmin, 
  onAssign, 
  tasks
}) => {
  const [selectedUserId, setSelectedUserId] = useState(ASSIGN_NONE);
  const [dueDate, setDueDate] = useState(null);
  const [dateError, setDateError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTaskTypes, setSelectedTaskTypes] = useState({});
  const { user } = useUser();

  const availableUsers = useMemo(() => {
    if (isAdmin) {
      const users = Object.values(userObject || {})
        .map(u => ({
          UserId: String(u?.userId ?? ''),
          userName: u?.userName ?? `User ${u?.userId ?? ''}`,
        }))
        .filter(u => u.UserId && !isExcludedTaskAssignee(u.userName));
      users.sort((a, b) => a.userName.localeCompare(b.userName));
      return users;
    }
    return [{
      UserId: String(user?.userId ?? ''),
      userName: user?.userName || 'Myself',
    }];
  }, [user?.userId, user?.userName, isAdmin]);

  // Calculate task breakdown by type (only incomplete tasks)
  const taskBreakdown = useMemo(() => {
    const breakdown = {};
    selectedItems.forEach(task => {
      // For individual tasks, check if they're completed
      if (task.DateCompleted) return;
      
      // For grouped tasks, we need to check if any underlying tasks are incomplete
      if (task.taskIds) {
        let incompleteCount = 0;
        task.taskIds.forEach(taskId => {
          const actualTask = tasks.find(t => t.id === taskId);
          if (actualTask && !actualTask.DateCompleted) {
            incompleteCount++;
          }
        });
        if (incompleteCount > 0) {
          const type = task.ReviewType || 'Unknown';
          breakdown[type] = (breakdown[type] || 0) + incompleteCount;
        }
      } else {
        // Individual task that's not completed
        const type = task.ReviewType || 'Unknown';
        breakdown[type] = (breakdown[type] || 0) + 1;
      }
    });
    return breakdown;
  }, [selectedItems, tasks]);

  const hasAssignedSelection = useMemo(() => {
    const isAssigned = (task) =>
      task?.Assignee != null && String(task.Assignee).trim() !== '';

    return selectedItems.some((task) => {
      if (task.taskIds) {
        return task.taskIds.some((taskId) => {
          const actualTask = tasks.find((t) => t.id === taskId);
          return actualTask && !actualTask.DateCompleted && isAssigned(actualTask);
        });
      }
      return !task.DateCompleted && isAssigned(task);
    });
  }, [selectedItems, tasks]);

  const handleAssign = async () => {
    // Validate date before proceeding
    if (dueDate && !validateDate(dueDate)) {
      return; // Don't proceed if date is invalid
    }
    
    logger.log('Assignment Details:', {
      selectedItemsCount: selectedItems.length,
      assignedTo: selectedUserId,
      assignedBy: user?.userId,
      dueDate,
      isAdmin,
      selectedTaskTypes,
    });
    
    setIsLoading(true);
    
    try {
      await handleTaskAssignment();
    } catch (error) {
      logger.error('Error updating assignments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTaskAssignment = async () => {
    // Prepare task objects - handle both individual tasks and grouped tasks
    let taskObjs = selectedItems.map(task => {
      if (task.taskIds) {
        // This is a grouped task, get the actual task objects but filter out completed tasks and unselected types
        return task.taskIds.map(taskId => {
          const actualTask = tasks.find(t => t.id === taskId);
          // Only include non-completed tasks and selected task types
          if (actualTask && !actualTask.DateCompleted) {
            const taskType = actualTask.ReviewType || 'Unknown';
            return isTaskTypeIncluded(selectedTaskTypes, taskType) ? actualTask : null;
          }
          return null;
        }).filter(Boolean);
      } else {
        // This is an individual task - filter by completion and selected type
        if (!task.DateCompleted) {
          const taskType = task.ReviewType || 'Unknown';
          return isTaskTypeIncluded(selectedTaskTypes, taskType) ? task : null;
        }
        return null;
      }
    }).flat().filter(Boolean);
    
    // Check if there are any tasks to assign after filtering out completed ones and unselected types
    if (taskObjs.length === 0) {
      const hasSelectedTypes = Object.keys(taskBreakdown).some(
        (type) => isTaskTypeIncluded(selectedTaskTypes, type)
      );
      if (!hasSelectedTypes) {
        toast.info('Please select at least one task type to assign.');
      } else {
        toast.info('No incomplete tasks found in selection. Completed tasks cannot be assigned.');
      }
      onClose();
      return;
    }
    
    // Store the count of tasks that will actually be assigned (before any additional filtering)
    const tasksToAssignCount = taskObjs.length;
    
    // For non-admin users, filter out tasks that are already assigned to them
    if (!isAdmin) {
      const originalCount = taskObjs.length;
      taskObjs = taskObjs.filter(taskObj => taskObj.Assignee !== user?.userId);
      if (taskObjs.length === 0) {
        toast.info('All selected tasks are already assigned to you');
        onClose();
        return;
      }
      if (taskObjs.length < originalCount) {
        toast.info(`${originalCount - taskObjs.length} tasks were already assigned to you and were skipped`);
      }
    }

    // Group by (MuniCode, Year, ReviewType, CaseType) so updates target the correct CaseAssignment rows
    const grouped = {};
    taskObjs.forEach(task => {
      const muniCode = task.ParcelID ? String(task.ParcelID).substring(0, 3) : 'UNK';
      const year = Number(task.Year);
      const reviewType = String(task.ReviewType || 'Unknown');
      const villageRun = task.villageRun || 0;
      if (!muniCode || !year || !reviewType) return;
      const key = `${muniCode}-${year}-${reviewType}-${villageRun}`;
      if (!grouped[key]) {
        grouped[key] = { MuniCode: muniCode, TaxYear: year, ReviewType: reviewType, villageRun, uids: [] };
      }
      if (task.ParcelID) grouped[key].uids.push(String(task.ParcelID));
    });

    // Prepare fields to update - make unassign branch authoritative
    const isUnassign = isUnassignSelection(selectedUserId);
    const fieldsToUpdate = {
      Assignee: isUnassign ? null : String(selectedUserId),
      DateDue: isUnassign ? null : (dueDate ?? null),
      Assigner: isUnassign ? null : (user?.userId ? String(user.userId) : null),
      DateAssigned: isUnassign ? null : new Date().toISOString().split('T')[0],
    };

    // Refactored: Send all group requests in parallel, collect results, and show a single summary toast
    const groupPayloads = Object.values(grouped)
      .filter(group =>
        group.MuniCode && group.TaxYear && group.ReviewType &&
        Array.isArray(group.uids) && group.uids.length > 0
      )
      .map(group => ({
        MuniCode: String(group.MuniCode),
        TaxYear: Number(group.TaxYear),
        reviewType: String(group.ReviewType),
        uids: group.uids.map(String),
        updateAllExcept: false,
        fieldsToUpdate,
        forceUpdateCompleted: false,
        villageRun: group.villageRun || 0,
      }));

    if (groupPayloads.length === 0) {
      toast.error('No valid groups to update. Please check your selection.');
      setIsLoading(false);
      return;
    }

    logger.log('Assignment API payloads (filtered taskObjs):', {
      taskCount: taskObjs.length,
      groupPayloads: groupPayloads.map((p) => ({
        reviewType: p.reviewType,
        uidCount: p.uids.length,
        uids: p.uids,
      })),
    });

    // Send all requests in parallel and collect results
    const results = await Promise.allSettled(
      groupPayloads.map(payload =>
        axiosInstanceNoToast.post('/update_tasks_generalized', payload)
          .then(res => ({ success: true, data: res.data, payload }))
          .catch(err => ({
            success: false,
            error: err?.response?.data?.error || err.message || 'Unknown error',
            payload
          }))
      )
    );

    // Summarize results
    const successes = results.filter(r => r.status === 'fulfilled' && r.value.success && r.value.data.success);
    const failures = results.filter(r => r.status === 'fulfilled' && !r.value.success);

    // Only show a single toast for all errors or all success
    if (failures.length > 0) {
      const errorMessages = failures.map(f =>
        `Group: ${JSON.stringify(f.value.payload)}\nError: ${f.value.error}`
      ).join('\n\n');
      toast.error(`Some assignments failed:\n${errorMessages}`);
    } else {
      toast.success(`${tasksToAssignCount} task${tasksToAssignCount > 1 ? 's' : ''} successfully ${isUnassignSelection(selectedUserId) ? 'unassigned' : 'assigned'}.`);
    }

    // Send assignment notification email if an assignee is selected
    if (hasAssigneeSelection(selectedUserId)) {
      try {
        const cases = taskObjs
          .filter((task) => task.ParcelID)
          .map((task) => ({
            parcel_id: task.ParcelID,
            review_type: task.ReviewType || 'Unknown',
            address_line_1: task.Address || 'No address',
            city: task.Town || 'No city',
            state: 'NY',
            zip_code: task.Zip5 || 'No zip',
            muni_code: task.ParcelID.substring(0, 3),
            tax_year: task.Year,
          }));

        if (cases.length > 0) {
          const notificationResult = await sendAssignmentNotification({
            assigneeId: selectedUserId,
            cases,
            assignerId: user?.userId,
            dueDate: dueDate,
          });
          
          if (notificationResult.success) {
            logger.log('Assignment notification email sent successfully');
          } else {
            logger.warn('Failed to send assignment notification email:', notificationResult.error);
            // Don't show error to user as the main operation succeeded
          }
        }
      } catch (emailError) {
        logger.error('Error sending assignment notification email:', emailError);
        // Don't show error to user as the main operation succeeded
      }
    } else if (isUnassignSelection(selectedUserId)) {
      // Handle unassign case - could send notification to previous assignee if needed
      logger.log('No assignee selected - unassigning tasks');
    }

    // Call the onAssign callback to refresh the data, passing the filtered tasks
    onAssign(isUnassignSelection(selectedUserId) ? '' : selectedUserId, dueDate, taskObjs);

    // Reset form
    setSelectedUserId(isAdmin ? ASSIGN_NONE : String(user?.userId ?? ''));
    setDueDate(null);
    setDateError('');
    setIsLoading(false);
    onClose();
  };

  const handleClose = () => {
    setSelectedUserId(isAdmin ? ASSIGN_NONE : String(user?.userId ?? ''));
    setDueDate(null);
    setDateError('');
    setIsLoading(false);
    setSelectedTaskTypes({});
    onClose();
  };

  // Calculate the actual number of items that can be assigned (only selected types)
  const actualItemCount = useMemo(() => {
    let count = 0;
    selectedItems.forEach(task => {
      if (task.taskIds) {
        // For grouped tasks, count only incomplete underlying tasks of selected types
        task.taskIds.forEach(taskId => {
          const actualTask = tasks.find(t => t.id === taskId);
          if (actualTask && !actualTask.DateCompleted) {
            const taskType = actualTask.ReviewType || 'Unknown';
            if (isTaskTypeIncluded(selectedTaskTypes, taskType)) {
              count++;
            }
          }
        });
      } else if (!task.DateCompleted) {
        const taskType = task.ReviewType || 'Unknown';
        if (isTaskTypeIncluded(selectedTaskTypes, taskType)) {
          count++;
        }
      }
    });
    return count;
  }, [selectedItems, tasks, selectedTaskTypes]);

  // For non-admin users, auto-select themselves (they can only pick up tasks)
  useEffect(() => {
    if (!open) {
      setSelectedUserId(isAdmin ? ASSIGN_NONE : String(user?.userId ?? ''));
      setDueDate(null);
      setDateError('');
      setIsLoading(false);
      setSelectedTaskTypes({});
    } else if (!isAdmin && user?.userId) {
      setSelectedUserId(String(user.userId));
    } else if (isAdmin && !hasAssignedSelection && isUnassignSelection(selectedUserId)) {
      setSelectedUserId(ASSIGN_NONE);
    }
  }, [open, isAdmin, user?.userId, hasAssignedSelection, selectedUserId]);

  // Validate date
  const validateDate = (date) => {
    if (!date) {
      setDateError('');
      return true;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      setDateError('Date must be in the future');
      return false;
    } else {
      setDateError('');
      return true;
    }
  };

  const getDialogTitle = () => {
    return isAdmin ? (isUnassignSelection(selectedUserId) ? 'Unassign Tasks' : 'Assign Tasks') : 'Pickup Tasks';
  };

  const getActionText = () => {
    return isAdmin ? (isUnassignSelection(selectedUserId) ? 'unassign' : 'assign') : 'pick up';
  };

  const getActionResultText = () => {
    if (!isAdmin) return 'picked up';
    return isUnassignSelection(selectedUserId) ? 'unassigned' : 'assigned';
  };

  const getItemType = () => {
    return 'task';
  };

  const getButtonText = () => {
    if (isAdmin && isUnassignSelection(selectedUserId)) {
      return 'Unassign';
    }
    return isAdmin ? 'Assign' : 'Pickup';
  };

  return (
    <StableMuiDialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 480,
          borderRadius: '16px',
          border: `0.5px solid ${colors.borderSecondary}`,
          bgcolor: colors.bgPrimary,
          m: 2,
        },
      }}
    >
      {open && (
        <Box sx={{ p: 3 }}>
          <Typography
            component="h2"
            sx={{ fontSize: 17, fontWeight: 500, color: colors.textPrimary, m: '0 0 4px' }}
          >
            {getDialogTitle()}
          </Typography>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary, m: '0 0 8px' }}>
            {actualItemCount === 0
              ? `No tasks selected to ${getActionText()}`
              : `${actualItemCount} task${actualItemCount === 1 ? '' : 's'} selected to ${getActionText()}`}
          </Typography>
          <Typography sx={{ fontSize: 12, color: colors.textTertiary, m: '0 0 20px', lineHeight: 1.5 }}>
            Completed tasks are filtered out automatically.
            {!isAdmin && ' Tasks already assigned to you will be skipped.'}
          </Typography>

          {Object.keys(taskBreakdown).length > 0 && (
            <>
              <SectionLabel>Task types</SectionLabel>
              <Box
                sx={{
                  border: `0.5px solid ${colors.borderTertiary}`,
                  borderRadius: '8px',
                  p: 1.75,
                  mb: 2.5,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary }}>
                    Select types to include
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const allSelected = {};
                        Object.keys(taskBreakdown).forEach((type) => {
                          allSelected[type] = true;
                        });
                        setSelectedTaskTypes(allSelected);
                      }}
                      sx={subtleButtonSx}
                    >
                      All
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const noneSelected = {};
                        Object.keys(taskBreakdown).forEach((type) => {
                          noneSelected[type] = false;
                        });
                        setSelectedTaskTypes(noneSelected);
                      }}
                      sx={subtleButtonSx}
                    >
                      None
                    </Button>
                  </Box>
                </Box>
                <FormGroup sx={{ gap: 0.25 }}>
                  {Object.entries(taskBreakdown).map(([type, count]) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox
                          checked={isTaskTypeIncluded(selectedTaskTypes, type)}
                          onChange={(e) => {
                            setSelectedTaskTypes((prev) => ({
                              ...prev,
                              [type]: e.target.checked,
                            }));
                          }}
                          size="small"
                          sx={{
                            color: colors.borderSecondary,
                            '&.Mui-checked': { color: colors.accent },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>
                          {type} ({count} {getItemType()}{count > 1 ? 's' : ''})
                        </Typography>
                      }
                      sx={{
                        mx: 0,
                        '& .MuiFormControlLabel-label': { fontSize: 13 },
                      }}
                    />
                  ))}
                </FormGroup>
                <Typography sx={{ fontSize: 12, color: colors.textTertiary, mt: 1.25, display: 'block' }}>
                  {actualItemCount} task{actualItemCount === 1 ? '' : 's'} will be {getActionResultText()}
                </Typography>
              </Box>
            </>
          )}

          <SectionLabel>Assignment details</SectionLabel>
          <Box
            sx={{
              border: `0.5px solid ${colors.borderTertiary}`,
              borderRadius: '8px',
              p: 1.75,
              mb: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              {isAdmin && (
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontSize: 12, color: colors.textSecondary, mb: 0.5, lineHeight: '18px' }}>
                    Assign to
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(String(e.target.value))}
                      displayEmpty
                      renderValue={(value) => {
                        if (value === ASSIGN_NONE) {
                          return (
                            <Typography component="span" sx={{ color: colors.textTertiary, fontSize: 13, fontWeight: 400 }}>
                              Select User
                            </Typography>
                          );
                        }
                        if (value === UNASSIGN) {
                          return (
                            <Typography component="span" sx={{ color: colors.error, fontSize: 13, fontWeight: 400 }}>
                              Unassign
                            </Typography>
                          );
                        }
                        return (
                          <Typography component="span" sx={{ fontSize: 13, fontWeight: 400 }}>
                            {availableUsers.find((u) => u.UserId === value)?.userName ?? value}
                          </Typography>
                        );
                      }}
                      id="assign-to-select"
                      sx={{
                        height: 36,
                        borderRadius: '8px',
                        fontSize: 13,
                        '& .MuiSelect-select': { py: 0.75, display: 'flex', alignItems: 'center' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.borderTertiary },
                      }}
                    >
                      <MenuItem value={ASSIGN_NONE} disabled sx={{ display: 'none' }} />
                      {hasAssignedSelection && (
                        <MenuItem value={UNASSIGN} sx={{ color: colors.error, fontSize: 13, fontWeight: 400 }}>
                          Unassign
                        </MenuItem>
                      )}
                      {hasAssignedSelection && (
                        <Divider sx={{ my: 0.5, borderColor: colors.borderTertiary }} />
                      )}
                      {availableUsers.map((u) => (
                        <MenuItem key={`user-${u.UserId}`} value={String(u.UserId)} sx={{ fontSize: 13, fontWeight: 400 }}>
                          {u.userName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontSize: 12, color: colors.textSecondary, mb: 0.5, lineHeight: '18px' }}>
                  Due date (optional)
                </Typography>
                <DatePicker
                  selected={dueDate ? new Date(dueDate) : null}
                  onChange={(date) => {
                    const dateString = date ? date.toISOString().split('T')[0] : null;
                    setDueDate(dateString);
                    validateDate(dateString);
                  }}
                  isClearable
                  dateFormat="MM/dd/yyyy"
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  popperPlacement="auto"
                  autoComplete="off"
                  popperClassName="datepicker-popper"
                  portalId="assignment-dialog-datepicker-portal"
                  withPortal
                  customInput={
                    <CustomDateInput
                      placeholder="Click to select a date"
                      style={{
                        height: '36px',
                        border: `0.5px solid ${dateError ? colors.error : colors.borderSecondary}`,
                        borderRadius: '8px',
                        fontSize: '13px',
                        background: colors.bgPrimary,
                      }}
                    />
                  }
                />
                {dateError && (
                  <Typography sx={{ fontSize: 12, color: colors.error, mt: 0.5 }}>
                    {dateError}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              pt: 0.5,
            }}
          >
            <Button
              onClick={handleClose}
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: colors.textSecondary,
                border: `0.5px solid ${colors.borderSecondary}`,
                borderRadius: '8px',
                px: 2,
                py: 1,
                textTransform: 'none',
                minWidth: 'auto',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              variant="contained"
              disableElevation
              disabled={isLoading || actualItemCount === 0 || (isAdmin && selectedUserId === ASSIGN_NONE)}
              sx={{
                fontSize: 13,
                fontWeight: 500,
                bgcolor: isAdmin && isUnassignSelection(selectedUserId) ? colors.error : colors.accent,
                borderRadius: '8px',
                px: 2.5,
                py: 1,
                textTransform: 'none',
                minWidth: 80,
                '&:hover': {
                  bgcolor: isAdmin && isUnassignSelection(selectedUserId) ? '#a82020' : '#144f8a',
                },
              }}
            >
              {isLoading ? <CircularProgress size={18} color="inherit" /> : getButtonText()}
            </Button>
          </Box>
        </Box>
      )}
    </StableMuiDialog>
  );
};

export default AssignmentDialog; 